import { getToken } from './token';

class WebSocketService {
  constructor() {
    this.echo = null;
    this.userId = null;
    this.messageCallbacks = [];
    this.isConnected = false;
    this.connectionAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
  }

  // Parse JWT token to get user ID
  parseJwtPayload(token) {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      // console.error("Could not parse JWT:", e);
      return null;
    }
  }

  // Initialize the websocket connection
  initialize() {
    const token = getToken();
    if (!token) {
      console.error("No authentication token found!");
      return false;
    }

    const payload = this.parseJwtPayload(token);
    const newUserId = payload ? (payload.sub || payload.id) : null;

    if (!newUserId) {
      console.error("Could not find user ID in the JWT token!");
      return false;
    }

    // Check if Pusher and Echo are available
    if (typeof window.Pusher === 'undefined' || typeof window.Echo === 'undefined') {
      return false;
    }

    // If already initialized and connected for the same user, keep existing connection
    if (this.echo && this.userId === newUserId && this.isConnected) {
      return true;
    }

    // If an existing connection exists, cleanly tear it down first
    if (this.echo) {
      this.disconnect();
    }

    this.userId = newUserId;

    try {
      // Configure Laravel Echo
      this.echo = new window.Echo.default({
        broadcaster: 'reverb',
        key: '435f9703f2982b2b9d2ed37c47d0689e',
        wsHost: 'api.campaigningtool.com',
        wsPort: 443,
        wssPort: 443,
        forceTLS: true,
        enabledTransports: ['ws', 'wss'],
        authEndpoint: 'https://api.campaigningtool.com/broadcasting/auth',
        auth: {
          headers: {
            'Authorization': 'Bearer ' + token,
            'Accept': 'application/json',
          }
        }
      });

      // Set up connection event listeners
      this.setupConnectionListeners();
      
      // Subscribe to the private channel
      this.subscribeToChannel();

      return true;
    } catch (error) {
      return false;
    }
  }

  // Set up connection event listeners
  setupConnectionListeners() {
    if (!this.echo || !this.echo.connector || !this.echo.connector.pusher) {
      return;
    }

    const pusher = this.echo.connector.pusher;

    pusher.connection.bind('connected', () => {
      this.isConnected = true;
      this.connectionAttempts = 0;
      this.notifyCallbacks('connected', { status: 'connected' });
    });

    pusher.connection.bind('error', (error) => {
      this.isConnected = false;
      this.notifyCallbacks('error', error);
    });

    pusher.connection.bind('disconnected', () => {
      this.isConnected = false;
      this.notifyCallbacks('disconnected', { status: 'disconnected' });
    });
  }

  // Handle reconnection logic
  handleReconnection() {
    if (this.connectionAttempts < this.maxReconnectAttempts) {
      this.connectionAttempts++;
      // console.log(`Attempting to reconnect... (${this.connectionAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.initialize();
      }, this.reconnectDelay * this.connectionAttempts);
    } else {
      // console.error('Max reconnection attempts reached');
      this.notifyCallbacks('maxReconnectAttemptsReached', { attempts: this.connectionAttempts });
    }
  }

  // Subscribe to the private channel
  subscribeToChannel() {
    if (!this.echo || !this.userId) {
      // console.error("Echo not initialized or user ID not found");
      return;
    }

    try {
      this.echo.private(`campaigningmessage.${this.userId}`)
        .listen('.CTchat', (e) => {
          console.log('%c🎯 [REVERB WS RAW EVENT RECEIVED]:', 'color: #00e5ff; font-weight: bold; font-size: 13px;', e);
          
          // Extract message data - try different possible structures
          let messageData = e;
          
          if (e.message) {
            messageData = e.message;
          } else if (e.data) {
            messageData = e.data;
          } else if (e.payload) {
            messageData = e.payload;
          }
          
          console.log('%c📦 [UNWRAPPED WS PAYLOAD]:', 'color: #76ff03; font-weight: bold;', messageData);

          // Notify all registered callbacks
          this.notifyCallbacks('message', messageData);
        });
      
      console.log(`%c✅ [WS Subscribed]: campaigningmessage.${this.userId}`, 'color: #00e676; font-weight: bold;');
    } catch (error) {
      console.error("❌ Failed to subscribe to channel:", error);
    }
  }

  // Register callback for message events
  onMessage(callback) {
    this.messageCallbacks.push(callback);
    return () => {
      // Return unsubscribe function
      const index = this.messageCallbacks.indexOf(callback);
      if (index > -1) {
        this.messageCallbacks.splice(index, 1);
      }
    };
  }

  // Register callback for connection events
  onConnection(callback) {
    this.messageCallbacks.push(callback);
    return () => {
      // Return unsubscribe function
      const index = this.messageCallbacks.indexOf(callback);
      if (index > -1) {
        this.messageCallbacks.splice(index, 1);
      }
    };
  }

  // Notify all registered callbacks
  notifyCallbacks(eventType, data) {
    this.messageCallbacks.forEach(callback => {
      try {
        callback(eventType, data);
      } catch (error) {
        // console.error("Error in websocket callback:", error);
      }
    });
  }

  // Disconnect from websocket
  disconnect() {
    try {
      if (this.echo) {
        if (this.userId) {
          try {
            this.echo.leave(`campaigningmessage.${this.userId}`);
          } catch (_) {}
        }
        if (this.echo.connector?.pusher) {
          const pusher = this.echo.connector.pusher;
          const state = pusher.connection?.state;
          if (state && state !== 'disconnected' && state !== 'unavailable') {
            pusher.disconnect();
          }
        } else if (typeof this.echo.disconnect === 'function') {
          this.echo.disconnect();
        }
      }
    } catch (_) {
      // Gracefully ignore closed states
    } finally {
      this.echo = null;
      this.isConnected = false;
      this.messageCallbacks = [];
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      userId: this.userId,
      connectionAttempts: this.connectionAttempts
    };
  }

  // Expose methods for testing (development only)
  testNotifyCallbacks(eventType, data) {
    if (process.env.NODE_ENV === 'development') {
      this.notifyCallbacks(eventType, data);
    }
  }
}

// Create a singleton instance
const websocketService = new WebSocketService();

export default websocketService; 